<template>
  <div class="packages-list">
    <div class="page-header">
      <h1 class="page-title">{{ $t("subscriptions.title") }}</h1>
      <!-- <the-breadcrumb :items="breadcrumbItems" /> -->
    </div>

    <!-- Tabs -->
    <div class="tabs-container">
      <div class="tabs">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'packages' }"
          @click="activeTab = 'packages'"
        >
          الباقات
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'subscriptions' }"
          @click="switchToSubscriptions"
        >
          اشتراكاتي
        </button>
      </div>
    </div>

    <div class="content-wrapper">
      <div v-if="loading" class="loading-container">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <!-- Packages Tab Content -->
      <div v-if="activeTab === 'packages'" class="tab-content">
        <div v-if="!loading && packages.length > 0" class="packages-grid">
          <div v-for="pkg in packages" :key="pkg.id" class="package-card">
            <div class="package-image">
              <img :src="pkg.image" :alt="pkg.name" />
            </div>

            <div class="package-content">
              <h3 class="package-name">{{ pkg.name }}</h3>

              <div class="package-info">
                <div class="info-item">
                  <i class="fas fa-tag"></i>
                  <span
                    >{{ pkg.total_with_tax }} {{ $t("common.currency") }}</span
                  >
                </div>

                <div class="info-item">
                  <i class="fas fa-clock"></i>
                  <span
                    >{{ pkg.duration }} {{ $t("subscriptions.months") }}</span
                  >
                </div>
              </div>

              <div v-if="pkg.price_after_discount" class="discount-badge">
                <span class="original-price"
                  >{{ pkg.price }} {{ $t("common.currency") }}</span
                >
                <span class="discount-label">{{
                  $t("subscriptions.discounted")
                }}</span>
              </div>

              <div class="package-footer">
                <base-button
                  :btnText="$t('subscriptions.viewDetails')"
                  :styleType="'primary_btn'"
                  @fireClick="goToPackageDetails(pkg.id)"
                  class="w-100"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="!loading" class="no-data">
          <img
            src="@/assets/media/empty_messages/search_message.png"
            alt="No packages"
          />
          <h3>{{ $t("subscriptions.noPackagesAvailable") }}</h3>
        </div>
      </div>

      <!-- Subscriptions Tab Content -->
      <div v-if="activeTab === 'subscriptions'" class="tab-content">
        <div
          v-if="!loading && subscriptions.length > 0"
          class="subscriptions-grid"
        >
          <div
            v-for="subscription in subscriptions"
            :key="subscription.id"
            class="subscription-card"
          >
            <div class="subscription-image">
              <img
                :src="subscription.package_image"
                :alt="subscription.package_name"
              />
            </div>

            <div class="subscription-content">
              <h3 class="subscription-name">{{ subscription.package_name }}</h3>

              <div class="subscription-info">
                <div class="info-item">
                  <i class="fas fa-calendar-alt"></i>
                  <span
                    >{{ $t("subscriptions.subscriptionDate") }}:
                    {{ formatDate(subscription.subscription_date) }}</span
                  >
                </div>

                <div class="info-item">
                  <i class="fas fa-calendar-check"></i>
                  <span
                    >{{ $t("subscriptions.expiryDate") }}:
                    {{ formatDate(subscription.expiry_date) }}</span
                  >
                </div>

                <div class="info-item">
                  <i class="fas fa-tag"></i>
                  <span
                    >{{ subscription.total_with_tax }}
                    {{ $t("common.currency") }}</span
                  >
                </div>

                <div class="info-item">
                  <i class="fas fa-clock"></i>
                  <span
                    >{{ subscription.package_duration }}
                    {{ $t("subscriptions.months") }}</span
                  >
                </div>
              </div>

              <div class="subscription-status">
                <span
                  class="status-badge"
                  :class="subscription.subscription_status"
                >
                  {{ subscription.status_text }}
                </span>
              </div>

              <div
                v-if="subscription.subscription_status === 'expired'"
                class="subscription-footer"
              >
                <base-button
                  :btnText="$t('subscriptions.renewSubscription')"
                  :styleType="'primary_btn'"
                  @fireClick="
                    $router.push(
                      `/subscriptions/packages/${subscription.package_id}`
                    )
                  "
                  class="w-100"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="!loading" class="no-data">
          <img
            src="@/assets/media/empty_messages/search_message.png"
            alt="No subscriptions"
          />
          <h3>{{ $t("subscriptions.noActiveSubscription") }}</h3>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TheBreadcrumb from "@/components/structure/TheBreadcrumb.vue";

export default {
  name: "PackagesList",

  components: {
    TheBreadcrumb,
  },

  data() {
    return {
      loading: false,
      activeTab: "packages",
      subscriptions: [],
    };
  },

  computed: {
    ...mapGetters("subscriptions", {
      packages: "allPackages",
      isLoading: "isLoading",
    }),

    breadcrumbItems() {
      return [
        { text: this.$t("common.home"), to: "/" },
        {
          text: this.$t("subscriptions.title"),
          to: { name: "CurrentSubscription" },
        },
        { text: this.$t("subscriptions.availablePackages"), active: true },
      ];
    },
  },

  mounted() {
    this.loadPackages();
  },

  methods: {
    ...mapActions("subscriptions", ["fetchPackages"]),

    async loadPackages() {
      this.loading = true;
      try {
        await this.fetchPackages();
      } catch (error) {
        this.$message.error(
          error.message || this.$t("subscriptions.errors.loadFailed")
        );
      } finally {
        this.loading = false;
      }
    },

    async loadSubscriptions() {
      this.loading = true;
      try {
        const response = await this.$axios.get("/subscriptions");
        this.subscriptions = response.data.data.data || [];
      } catch (error) {
        this.$message.error(
          error.message || this.$t("subscriptions.errors.loadFailed")
        );
      } finally {
        this.loading = false;
      }
    },

    switchToSubscriptions() {
      this.activeTab = "subscriptions";
      if (this.subscriptions.length === 0) {
        this.loadSubscriptions();
      }
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("ar-EG");
    },

    goToPackageDetails(packageId) {
      this.$router.push({
        name: "PackageDetails",
        params: { id: packageId },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.packages-list {
  padding: 20px;

  .page-header {
    margin-bottom: 30px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 10px;
      color: var(--theme_text_clr);
    }
  }

  .content-wrapper {
    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 400px;
    }

    .packages-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 24px;

      .package-card {
        background: var(--theme_second_bg_clr);
        border-radius: 12px;
        box-shadow: 0 2px 8px var(--simple_shadow_clr);
        overflow: hidden;
        transition: transform 0.3s ease, box-shadow 0.3s ease;

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 4px 16px var(--mid_shadow_clr);
        }

        .package-image {
          position: relative;
          height: 200px;
          overflow: hidden;
          background: var(--theme_main_bg_clr);

          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        }

        .package-content {
          padding: 20px;

          .package-name {
            font-size: 20px;
            font-weight: 600;
            color: var(--theme_text_clr);
            margin-bottom: 15px;
          }

          .package-info {
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-bottom: 15px;

            .info-item {
              display: flex;
              align-items: center;
              gap: 10px;
              font-size: 14px;
              color: var(--theme_secondary_text_clr);

              i {
                color: var(--main_theme_clr);
                font-size: 16px;
              }
            }
          }

          .discount-badge {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 15px;

            .original-price {
              text-decoration: line-through;
              color: #999;
              font-size: 14px;
            }

            .discount-label {
              background: #ff6b6b;
              color: #fff;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
              font-weight: 600;
            }
          }

          .package-footer {
            margin-top: 20px;
          }
        }
      }
    }

    .no-data {
      text-align: center;
      padding: 60px 20px;
      background: var(--theme_second_bg_clr);
      border-radius: 12px;
      box-shadow: 0 2px 8px var(--simple_shadow_clr);

      img {
        width: 200px;
        margin-bottom: 30px;
      }

      h3 {
        font-size: 22px;
        font-weight: 600;
        color: var(--theme_text_clr);
      }
    }
  }

  .tabs-container {
    margin-bottom: 30px;
    background: var(--theme_second_bg_clr);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--simple_shadow_clr);
    padding: 10px;

    .tabs {
      display: flex;
      gap: 10px;

      .tab-btn {
        flex: 1;
        padding: 12px 24px;
        border: none;
        background: transparent;
        color: var(--theme_secondary_text_clr);
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        border-radius: 8px;
        transition: all 0.3s ease;

        &:hover {
          background: var(--theme_main_bg_clr);
          color: var(--theme_text_clr);
        }

        &.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #fff;
        }
      }
    }
  }

  .tab-content {
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .subscriptions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 24px;

    .subscription-card {
      background: var(--theme_second_bg_clr);
      border-radius: 12px;
      box-shadow: 0 2px 8px var(--simple_shadow_clr);
      overflow: hidden;
      transition: transform 0.3s ease, box-shadow 0.3s ease;

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 4px 16px var(--mid_shadow_clr);
      }

      .subscription-image {
        position: relative;
        height: 200px;
        overflow: hidden;
        background: var(--theme_main_bg_clr);

        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      .subscription-content {
        padding: 20px;

        .subscription-name {
          font-size: 20px;
          font-weight: 600;
          color: var(--theme_text_clr);
          margin-bottom: 15px;
        }

        .subscription-info {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 15px;

          .info-item {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 14px;
            color: var(--theme_secondary_text_clr);

            i {
              color: var(--main_theme_clr);
              font-size: 16px;
              width: 20px;
            }
          }
        }

        .subscription-status {
          margin-top: 15px;

          .status-badge {
            display: inline-block;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 600;

            &.active {
              background: #d4edda;
              color: #155724;
            }

            &.expired {
              background: #f8d7da;
              color: #721c24;
            }

            &.inactive {
              background: #e2e3e5;
              color: #383d41;
            }
          }
        }
      }
    }
  }
}
</style>
